import { RefObject, useEffect, useId, useRef, useState } from 'react'

export interface UsePacContainerExpandedResult {
  isExpanded: boolean
  pacListboxId: string
}

/**
 * Observes the Google Places `.pac-container` dropdown that belongs to the
 * provided input and exposes its visibility / listbox id so the consumer can
 * wire `aria-expanded` and `aria-controls` per instance.
 *
 * Google Places appends one `.pac-container` to `document.body` for every
 * `new google.maps.places.Autocomplete(input)` call (sometimes lazily on first
 * focus). We snapshot the existing containers on mount, then claim the next
 * new one as ours via a `MutationObserver` on `document.body`.
 */
export default function usePacContainerExpanded(
  // The ref is accepted for future scoping (e.g. spatial proximity) and to
  // make the hook contract explicit per-input. Currently it is unused at
  // runtime because the snapshot-and-diff strategy is sufficient.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  inputRef: RefObject<HTMLInputElement | null>
): UsePacContainerExpandedResult {
  const reactId = useId()
  const pacListboxId = `pac-listbox-${reactId.replace(/:/g, '')}`
  const [isExpanded, setIsExpanded] = useState(false)
  const claimedRef = useRef<Element | null>(null)

  useEffect(() => {
    let pacObserver: MutationObserver | null = null

    const adoptPacContainer = (pacContainer: Element): void => {
      if (claimedRef.current !== null) return
      claimedRef.current = pacContainer

      pacContainer.setAttribute('id', pacListboxId)
      if (pacContainer.getAttribute('role') === null) {
        pacContainer.setAttribute('role', 'listbox')
      }

      const checkVisibility = (): void => {
        const style = window.getComputedStyle(pacContainer)
        setIsExpanded(style.display !== 'none')
      }

      checkVisibility()

      pacObserver = new MutationObserver(checkVisibility)
      pacObserver.observe(pacContainer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }

    const existingContainers = new Set<Element>(
      Array.from(document.querySelectorAll('.pac-container'))
    )

    const findUnclaimedContainer = (): Element | null => {
      const all = document.querySelectorAll('.pac-container')
      for (const el of Array.from(all)) {
        if (!existingContainers.has(el)) return el
      }

      return null
    }

    const tryAdopt = (): void => {
      if (claimedRef.current !== null) return
      const candidate = findUnclaimedContainer()
      if (candidate !== null) adoptPacContainer(candidate)
    }

    tryAdopt()

    const bodyObserver = new MutationObserver(tryAdopt)
    bodyObserver.observe(document.body, { childList: true })

    return () => {
      pacObserver?.disconnect()
      bodyObserver.disconnect()
      claimedRef.current = null
    }
  }, [pacListboxId])

  return { isExpanded, pacListboxId }
}
